'use client';

import { socketIo } from '@/contexts/NotificationProvider';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { MessageSquare } from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

const TOAST_ID = 'bot-inbox-alert';
const NOTIF_PERMISSION_KEY = 'mb_bot_notif_asked';

// Two-tone ascending chime: C5 → E5
export function playBotChime() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const notes = [
      { freq: 523.25, t: 0, vol: 0.22 },
      { freq: 659.25, t: 0.14, vol: 0.18 },
    ];
    notes.forEach(({ freq, t, vol }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      const start = ctx.currentTime + t;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(vol, start + 0.008);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.22);
      osc.start(start);
      osc.stop(start + 0.22);
    });
    setTimeout(() => ctx.close(), 2000);
  } catch {}
}

function fireBrowserNotification(title: string, body: string) {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  const show = () => {
    try {
      const n = new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag: 'bot-inbox', // replaces previous notification with same tag
        requireInteraction: false,
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
      setTimeout(() => n.close(), 8000);
    } catch {}
  };

  if (Notification.permission === 'granted') {
    show();
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission().then((p) => {
      if (p === 'granted') show();
    });
  }
}

function BotToast({
  message,
  unread,
  onNavigate,
}: {
  message: string;
  unread: number;
  onNavigate: () => void;
}) {
  return (
    <div className='flex items-start gap-3'>
      <div className='mt-0.5 flex-shrink-0 w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center shadow-sm'>
        <MessageSquare className='w-4 h-4 text-white' />
      </div>
      <div className='flex-1 min-w-0'>
        <p className='font-semibold text-sm text-gray-900 leading-snug'>
          {unread > 1 ? `${unread} new visitor messages` : 'Visitor needs your help'}
        </p>
        <p className='text-xs text-gray-500 mt-0.5 truncate max-w-[220px]'>{message}</p>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate();
          }}
          className='mt-2 inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700 transition-colors'
        >
          Open Inbox →
        </button>
      </div>
    </div>
  );
}

export default function BotInboxNotifier() {
  const vendorId = useWorkspace()?.activeWorkspace?.vendorid;
  const pathname = usePathname();
  const router = useRouter();

  const pathnameRef = useRef(pathname);
  const unreadRef = useRef(0);
  const defaultTitleRef = useRef<string>('');

  // Keep ref current so socket handlers don't need re-attachment on navigation
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    if (typeof document !== 'undefined' && !defaultTitleRef.current) {
      defaultTitleRef.current = document.title;
    }
  }, []);

  // Request browser notification permission once (on first load, proactively)
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'default') return;
    if (localStorage.getItem(NOTIF_PERMISSION_KEY)) return;
    localStorage.setItem(NOTIF_PERMISSION_KEY, '1');
    // Small delay so it doesn't fire at the same instant the page loads
    const t = setTimeout(() => Notification.requestPermission(), 3000);
    return () => clearTimeout(t);
  }, []);

  // Join bot_inbox room globally — persists across page navigations
  useEffect(() => {
    if (!vendorId) return;
    const room = `bot_inbox_${vendorId}`;
    const join = () => socketIo.emit('join_room', { room });

    if (socketIo.connected) join();
    else socketIo.once('connect', join);

    // Re-join on every reconnect
    socketIo.on('connect', join);
    return () => {
      socketIo.off('connect', join);
      // Intentionally do NOT leave_room — stays subscribed for cross-page notifications
    };
  }, [vendorId]);

  // Dismiss toast and reset count when user arrives at /bot
  useEffect(() => {
    const isBot = pathname === '/bot' || pathname?.startsWith('/bot/');
    if (isBot) {
      unreadRef.current = 0;
      toast.dismiss(TOAST_ID);
      if (typeof document !== 'undefined' && defaultTitleRef.current) {
        document.title = defaultTitleRef.current;
      }
    }
  }, [pathname]);

  // Global socket event listeners
  useEffect(() => {
    if (!vendorId) return;

    const handleIncoming = (message: string) => {
      const isBot =
        pathnameRef.current === '/bot' || pathnameRef.current?.startsWith('/bot/');
      const pageVisible = typeof document !== 'undefined' && document.visibilityState === 'visible';
      const pageFocused = typeof document !== 'undefined' && document.hasFocus();

      // Always play sound — even when on /bot (same as chat app behaviour)
      playBotChime();

      // Browser notification: when page is not focused or user is on another page
      if (!isBot || !pageFocused || !pageVisible) {
        const count = ++unreadRef.current;
        if (typeof document !== 'undefined' && defaultTitleRef.current) {
          document.title = `(${count}) New visitor${count > 1 ? 's' : ''} — ${defaultTitleRef.current}`;
        }
        const title =
          count > 1 ? `${count} visitor messages waiting` : 'Visitor needs your help';
        fireBrowserNotification(title, message);

        // Toast only when NOT on /bot page
        if (!isBot) {
          const navigate = () => {
            unreadRef.current = 0;
            toast.dismiss(TOAST_ID);
            router.push('/bot');
          };

          const content = (
            <BotToast message={message} unread={count} onNavigate={navigate} />
          );

          if (toast.isActive(TOAST_ID)) {
            toast.update(TOAST_ID, { render: content });
          } else {
            toast(content, {
              toastId: TOAST_ID,
              autoClose: 12000,
              position: 'bottom-right',
              closeOnClick: false,
              pauseOnHover: true,
              onClose: () => {
                unreadRef.current = 0;
              },
            });
          }
        }
      }
    };

    // Visitor chatting directly with SDR (after human transfer)
    const handleVisitorMessage = (data: { threadId: string; query: string; timestamp: string }) => {
      handleIncoming(data.query || 'New visitor message');
    };

    // Visitor requested human agent (high priority)
    const handleNewChat = (data: {
      threadId: string;
      lastQuery: string;
      email: string;
      timestamp: string;
    }) => {
      handleIncoming(
        data.lastQuery ||
          (data.email ? `${data.email} requested assistance` : 'Visitor requested human agent'),
      );
    };

    socketIo.on('bot:visitor_message', handleVisitorMessage);
    socketIo.on('bot:new_chat', handleNewChat);
    socketIo.on('vendor:new-chat', handleNewChat);

    return () => {
      socketIo.off('bot:visitor_message', handleVisitorMessage);
      socketIo.off('bot:new_chat', handleNewChat);
      socketIo.off('vendor:new-chat', handleNewChat);
    };
  }, [vendorId, router]);

  return null;
}
